import { getDateTime, shortDate, addDateWithYears } from './date';
import { Api } from './interface';
import Excel from 'exceljs';
import * as FileSaver from 'file-saver';
import { groupSelectError } from './common';
import { validateField } from './validator';
import { bake } from './common';
import { excelDateToJSDate } from './common.js';
import { toast } from 'react-toastify';
import { IsRole } from './validator.js';

let invalidMeta = [];
let locations = [];
let poNumbers = [];
let ioNumbers = [];
let users = [];
/**
 * Validates PO data structure and required fields
 * @param {Array} data - Array of PO data objects
 * @param {Array} requiredKeys - Array of required field names
 * @returns {Array} Array of validation errors
 */
function validatePOData(data, requiredKeys) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const firstRow = data[0];
  const missingKeys = requiredKeys.filter((key) => !(key in firstRow));
  return missingKeys.length > 0 ? [{ row: 1, keys: missingKeys }] : [];
}

/**
 * Converts Excel date numbers to formatted date strings
 * @param {Array} data - Array of PO data objects
 * @param {string} dateField - Name of the date field to convert
 * @returns {Array} Data with converted dates
 */
function convertPODates(data, dateField = 'Document Date') {
  return data.map((item) => ({
    ...item,
    [dateField]: item[dateField]
      ? shortDate(excelDateToJSDate(item[dateField]))
      : null,
  }));
}

/**
 * Sums quantities for duplicate PO entries based on composite key
 * @param {Array} data - Array of PO data objects
 * @param {Array} keyFields - Fields to create composite key
 * @param {string} quantityField - Field containing quantity to sum
 * @returns {Array} Data with TotalQuantity field added
 */
function sumPOQuantities(
  data,
  keyFields = ['Order', 'Purchasing Document', 'Short Text'],
  quantityField = 'Order Quantity'
) {
  // Create sum map
  const sumMap = {};
  for (const item of data) {
    const key = keyFields.map((field) => item[field]).join('_');
    const qty = parseFloat(item[quantityField]) || 0;
    sumMap[key] = (sumMap[key] || 0) + qty;
  }

  // Attach sum to each item
  return data.map((item) => {
    const key = keyFields.map((field) => item[field]).join('_');
    return {
      ...item,
      TotalQuantity: sumMap[key],
    };
  });
}

/**
 * Removes duplicate PO entries based on composite key
 * @param {Array} data - Array of PO data objects
 * @param {Array} keyFields - Fields to create composite key for deduplication
 * @returns {Array} Deduplicated data
 */
function removePODuplicates(
  data,
  keyFields = ['Order', 'Purchasing Document', 'Short Text']
) {
  const filteredData = [];
  const seen = new Set();

  for (const item of data) {
    const key = keyFields.map((field) => item[field]).join('_');
    if (!seen.has(key)) {
      filteredData.push(item);
      seen.add(key);
    }
  }

  return filteredData;
}

/**
 * Removes header row if present in data
 * @param {Array} data - Array of PO data objects
 * @param {string} headerIdentifier - Value in first column that identifies header row
 * @returns {Array} Data without header row
 */
function removeHeaderRow(data, headerIdentifier = 'Order') {
  if (data.length && data[0]['Order'] === headerIdentifier) {
    return data.slice(1);
  }
  return data;
}

/**
 * Complete PO data preprocessing pipeline
 * @param {Array} rawData - Raw PO data from Excel
 * @param {Object} options - Processing options
 * @returns {Array} Processed PO data
 */
function preprocessPOData(rawData, options = {}) {
  const {
    requiredKeys = [
      'Order',
      'IO Description ',
      'Purchasing Document',
      'Document Date',
      'PO Title',
      'Order Quantity',
    ],
    dateField = 'Document Date',
    keyFields = ['Order', 'Purchasing Document', 'Short Text'],
    quantityField = 'Order Quantity',
    headerIdentifier = 'Order',
  } = options;

  // Validate required fields
  const validationErrors = validatePOData(rawData, requiredKeys);
  if (validationErrors.length > 0) {
    throw new Error(
      `Validation failed: Missing required fields - ${validationErrors[0].keys.join(', ')}`
    );
  }

  // Apply preprocessing pipeline
  let processedData = rawData;

  // Convert dates
  processedData = convertPODates(processedData, dateField);

  // Sum quantities for duplicates
  processedData = sumPOQuantities(processedData, keyFields, quantityField);

  // Remove duplicates
  processedData = removePODuplicates(processedData, keyFields);

  // Remove header row
  processedData = removeHeaderRow(processedData, headerIdentifier);

  return processedData;
}

// prepare excel for template downoad
const templateDownloadBulkEdit = (
  fields,
  identifyingFields,
  selectedEntityTypes,
  selectedIdentifierFields,
  selectedToUpdateFields,
  parentLocations,
  allUsers,
  level
) => {
  let dbColumns = [];

  locations = parentLocations;
  users = allUsers;

  // fetch grouped values to provide validaion in excel
  Api({
    sp: 'getMetaOptionsByTypeBulkUpdate',
    json: { types: selectedEntityTypes },
  }).then((response) => {
    // fetch the existing valid values for the selected identifier fields
    // users should select the identification value from dropdown in excel
    Api({
      sp: 'getIdentifierValuesBulkUpdate',
      json: {
        selectedIdentifierFields: identifyingFields,
        types: selectedEntityTypes,
      },
    }).then((identifierValues) => {
      // map the values to the identifier fields
      fields
        .filter((field) =>
          selectedIdentifierFields
            .map((selectedField) => selectedField.UniqueEntityMetaType_DBID)
            .includes(field.EntityMetaType_DBID)
        )
        // verify if the identifier or identifier by field is part of the selected entity type template
        .filter((f) =>
          selectedEntityTypes
            .map((n) => n.EntityType)
            .some((item) => f.Template?.split(',').includes(item))
        )
        // mark which ones are identifier to avoid updating the identifier data
        .forEach((x) => dbColumns.push({ ...x, Identifier: true }));
      // add the "to update" in dbColumns for header row
      selectedToUpdateFields.forEach((x) => dbColumns.push(x));

      if (
        level === '' &&
        selectedToUpdateFields.filter(
          (field) =>
            field.EntityMetaType === 'PO Number' ||
            field.EntityMetaType === 'IO Number'
        ).length
      ) {
        Api({
          sp: 'getPurchaseOrders',
          json: {},
        }).then((pos) => {
          poNumbers = pos;
          Api({
            sp: 'getInternalOrders',
            json: {},
          }).then((ios) => {
            ioNumbers = ios;
            downloadTemplate(
              selectedEntityTypes,
              dbColumns,
              response,
              selectedIdentifierFields,
              identifierValues
            );
          });
        });
      } else {
        downloadTemplate(
          selectedEntityTypes,
          dbColumns,
          response,
          selectedIdentifierFields,
          identifierValues
        );
      }
    });
  });
};

const downloadTemplate = (
  selectedEntityTypes,
  dbColumns,
  metaOptions,
  selectedIdentifierFields,
  identifierValues
) => {
  const workbook = new Excel.Workbook(); // Create a new workbook
  const worksheet = workbook.addWorksheet('data');
  const worksheet2 = workbook.addWorksheet('validation');
  let colList = [];
  let obj = {};
  let hasIOPO = false;

  // add a column for selected entity types
  colList.push({ header: 'Type', key: 'EntityType' });
  obj['EntityType'] = 'Select Type';
  let options = [];
  selectedEntityTypes.map((entType) => {
    options.push(entType.EntityType);
  });
  // create validation for selected entity types in the workbook
  validationList(
    Object.keys(obj).length,
    worksheet,
    worksheet2,
    options,
    'EntityType'
  );

  // add a column to select the identifier meta type for each row (e.g., Etag)
  colList.push({ header: 'Identifier', key: 'EntityMetaType' });
  obj['EntityMetaType'] = 'Select Identifier';
  options = [];
  selectedIdentifierFields.map((identifier) => {
    options.push(identifier.EntityMetaType);
  });
  validationList(
    Object.keys(obj).length,
    worksheet,
    worksheet2,
    options,
    'EntityMetaType'
  );
  // add a column to select the value of the identifier meta type (e.g., Cxxxx if selected Identifier is Etag)
  colList.push({ header: 'Identifier Values', key: 'EntityMetaValue' });
  obj['EntityMetaValue'] = 'Select Identifier Value';
  options = [];
  identifierValues.map((identifier) => {
    options.push(identifier.EntityMetaValue);
  });
  // create validation for selected identifier meta type in the workbook to avoid creating new identification values in bulk edit
  // the identification value must pre-exit
  validationList(
    Object.keys(obj).length,
    worksheet,
    worksheet2,
    options,
    'EntityMetaType'
  );

  dbColumns.forEach((col) => {
    // if any "to update" field need to be unique by different field
    // add the "unique by" field in the head and update the label
    let key = col.Identifier
      ? col.EntityMetaType + ' (Identified By)'
      : col.EntityMetaType;
    if (col.DataType !== 'Image') {
      colList.push({ header: key, key: key });
      // validation list for locations
      if (col.EntityMetaType === 'Location') {
        obj[key] = 'Select Location';
        let options = [];
        locations.map((location) => {
          options.push(location.Path);
        });
        validationList(
          Object.keys(obj).length,
          worksheet,
          worksheet2,
          options,
          'Location'
        );
        // provide sample format for all the columns in the excel
      } else if (col.EntityMetaType === 'PO Number' && poNumbers.length) {
        hasIOPO = true;
        obj[key] = 'Select PO Number';
        let options = [];
        poNumbers.map((po) => {
          const description = po.pi_description
            ? ` - ${po.pi_description}`
            : '';
          return options.push(`${po.po_num}${description}`);
        });
        validationList(
          Object.keys(obj).length,
          worksheet,
          worksheet2,
          options,
          'PO Number'
        );
      } else if (col.EntityMetaType === 'IO Number' && ioNumbers.length) {
        hasIOPO = true;
        obj[key] = 'Select IO Number';
        let options = ioNumbers.map((io) => {
          const description = io.io_description
            ? ` - ${io.io_description}`
            : '';
          return `${io.io_num}${description}`;
        });

        validationList(
          Object.keys(obj).length,
          worksheet,
          worksheet2,
          options,
          'IO Number'
        );
      } else if (col.DataType === 'String') {
        obj[key] = 'Text...';
      } else if (col.DataType === 'Integer') {
        obj[key] = '12';
      } else if (col.DataType === 'Float') {
        obj[key] = '1.2';
      } else if (
        col.DataType === 'Group' ||
        col.DataType === 'Multiple Choice'
      ) {
        obj[key] = 'Select Option';
        let options = [];
        metaOptions.forEach((y) => {
          if (key === y.EntityMetaType) {
            options.push(y.EntityMetaOption);
          }
        });
        validationList(colList.length, worksheet, worksheet2, options, key);
      } else if (col.DataType === 'Binary') {
        obj[key] = '0(or 1)';
      } else if (col.DataType === 'URL') {
        obj[key] = 'www.myurl.com';
      } else if (col.DataType === 'Email') {
        obj[key] = 'name@intel.com';
      } else if (col.DataType === 'Date') {
        obj[key] = '12/30/2019 (mm/dd/yyyy)';
      } else if (col.DataType === 'Phone Number') {
        obj[key] = '123-123-1234';
      } else if (col.DataType === 'User Lookup') {
        // validation for any user type field such as owner
        obj[key] = 'Select User';
        let options = [];
        users.map((user) => {
          options.push(user.UserName);
        });
        validationList(
          Object.keys(obj).length,
          worksheet,
          worksheet2,
          options,
          'EntityOwner'
        );
      }
      // format for meta type (e.g., notes) which can be multiple to allow users to provide multiple values in one row
      if (col.AllowMultiple) {
        obj[key] = obj[key] + '|' + obj[key];
      }
    }
    // create a validation list for dropdown in excel if the column is an identifier field
    if (col.Identifier) {
      obj[key] = 'Select Option';
      let options = [];
      identifierValues.forEach((y) => {
        if (col.EntityMetaType === y.EntityMetaType) {
          options.push(y.EntityMetaValue);
        }
      });
      validationList(colList.length, worksheet, worksheet2, options, key);
    }
  });
  if (hasIOPO) {
    // Use utility function for PO-IO mapping
    const worksheet3 = workbook.addWorksheet('PO_IO_Mapping');
    createAdvancedPOIOMapping(worksheet3, poNumbers, ioNumbers);
  }
  worksheet.columns = colList;
  worksheet.addRow(obj);
  worksheet2.columns = colList;

  workbook.xlsx.writeBuffer().then(function (data) {
    var blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(blob, 'Bulk_Update_Format_' + getDateTime() + '.xlsx');
  });
};

// create validation list for excel
const validationList = (position, sheet1, sheet2, options, key) => {
  let letter = sheet1.getColumn(position).letter;

  let address = `${letter}2:${letter}1048576`;
  let range = `validation!$${letter}$2:$${letter}$${options.length + 1}`;
  sheet1.dataValidations.add(address, {
    type: 'list',
    allowBlank: true,
    formulae: [range],
    showErrorMessage: true,
    errorStyle: 'error',
    error:
      'The value does not match the data validation restrictions defined for this cell',
  });

  sheet2.getColumn(position).values = [key, ...options];
};
// Add this helper function near the top of the class, after the constructor
const removeCapitalAssetColumns = (dbColumns) => {
  const columnsToRemove = ['IO Number', 'PO Number', 'CTag'];

  // Add Company Code in columns to remove list if the user is not Admin
  if (!IsRole(['Admin'])) {
    columnsToRemove.push('Company Code');
  }

  return dbColumns.filter(
    (column) =>
      !columnsToRemove.some(
        (removeCol) =>
          column.EntityMetaType &&
          column.EntityMetaType.toLowerCase() === removeCol.toLowerCase()
      )
  );
};

const processUploadBulkEdit = (
  selectedEntityTypes,
  metaOptions,
  excelRows,
  colList,
  selectedToUpdateFields,
  level,
  entitiesFound,
  parentLocations,
  allUsers,
  canEdit,
  periodicityFetchFlag
) => {
  invalidMeta = [];
  locations = parentLocations;
  users = allUsers;

  // fetch current user from browser local storage to verify the permissions
  let currentUser = users.filter(
    (user) => user?.OID === bake({ key: 'user' })['oid']
  );

  // verify all the entity types provided in excel are of selected types in the modal
  let typesNotSelected = excelRows.filter(
    (row) =>
      !selectedEntityTypes
        .map((entityType) => entityType.EntityType)
        .includes(row['type'])
  );
  if (typesNotSelected?.length) {
    let errMsg =
      'Row(s) :' +
      typesNotSelected.map((type) => type.rowNum + 1) +
      ' have unselected types';
    invalidMeta.push(errMsg);
  }
  let uniqueToUpdateMetaType = {};

  // get the list of "unique" field (or combination thereof) in the "to update" list
  if (selectedToUpdateFields.filter((field) => field.IsUnique)) {
    let toUpdateUniqueField = selectedToUpdateFields.filter(
      (field) => field.IsUnique
    )[0];
    // create an object of "unique" and "unique by" meta
    // this object will have all the details of the meta, i.e., EntityMetaType, DataType, etc.
    // this object will be used to verify against all the rows in the excel and not in db
    if (toUpdateUniqueField) {
      let toUpdateUnique = {
        uniqueToUpdateField: toUpdateUniqueField,
        // if there's no "unique by" use the "unique" itself
        uniqueByField: selectedToUpdateFields.filter(
          (field) =>
            field.EntityMetaType_DBID ===
              toUpdateUniqueField?.UniqueEntityMetaType_DBID ??
            toUpdateUniqueField?.EntityMetaType_DBID
        )[0],
      };

      // create an object of only EntityMetaType of unique fields to send to db to verify against existing values in db
      // this object will be used to verify against data in db
      uniqueToUpdateMetaType = {
        ...uniqueToUpdateMetaType,
        uniqueToUpdateField: toUpdateUniqueField.EntityMetaType,
      };
      // if there's a "unique by" add the property to the object
      if (toUpdateUnique?.uniqueByField?.EntityMetaType)
        uniqueToUpdateMetaType = {
          ...uniqueToUpdateMetaType,
          uniqueByField: toUpdateUnique?.uniqueByField?.EntityMetaType,
        };

      // check for duplicate rows in excel n case of unique "to update" fields
      checkExcelDataUniqueValidity(toUpdateUnique, excelRows);
    }
  }

  // if any record in excel is not found, add the rows to the error report
  let notFound = excelRows.filter((row) => {
    return !entitiesFound.filter(
      (item) =>
        item.EntityType === row['type'] &&
        item.EntityMetaType === row['identifier'] &&
        item.EntityMetaValue === row['value']
    ).length;
  });
  if (notFound?.length) {
    let errMsg =
      'Row(s) :' + notFound.map((entry) => entry.rowNum + 1) + ' do not exist';
    invalidMeta.push(errMsg);
  }

  // if any permission is required, add to the error report
  if (!canEdit) {
    let unauthorizedEntities = excelRows.filter((row) => {
      return !entitiesFound.filter(
        (item) =>
          item.EntityOwnerName !== currentUser?.UserName &&
          item.Entity_DBID === row.Entity_DBID
      ).length;
    });
    if (unauthorizedEntities?.length) {
      let errMsg =
        'Row(s) :' +
        unauthorizedEntities.map((entry) => entry.rowNum + 1) +
        ' not owned by user. Proper role(s) required';
      invalidMeta.push(errMsg);
    }
  }
  // attach entity_dbid to the "found" entries in excel
  excelRows = excelRows.map((row) => {
    let item = entitiesFound.filter((item) => item.rowNum === row['rowNum']);

    return item?.length === 1
      ? { ...row, Entity_DBID: item[0].Entity_DBID }
      : row;
  });

  return processData(
    selectedToUpdateFields,
    colList,
    excelRows,
    metaOptions,
    level,
    uniqueToUpdateMetaType,
    periodicityFetchFlag
  );
};

// verify the validity of each value in each "to update" column
const processData = (
  selectedToUpdateFields,
  colList,
  excelRows,
  metaOptions,
  level,
  uniqueToUpdateMetaType,
  periodicityFetchFlag
) => {
  let fileColumns = colList,
    dbColumns = selectedToUpdateFields,
    finalJSON = [],
    keys = [],
    isName = 0,
    isLocation = 0;

  // attach meta options to the "to update" field if it's a grouped field, such as "Model" to verify the data
  dbColumns.forEach((c) => {
    if (c.DataType === 'Group' || c.DataType === 'Multiple Choice') {
      c.options = [];

      metaOptions.forEach((y) => {
        if (c.EntityMetaType === y.EntityMetaType) {
          c.options.push(y.EntityMetaOption);
        }
      });
    }
  });

  // loop through entire excel data to verify data based on data type
  excelRows.forEach((row, i) => {
    let obj = { Entity_DBID: row['Entity_DBID'], rowNum: row['rowNum'] };
    if (row[i] && typeof row[i] === 'string') {
      row[i] = row[i].trim();
    }

    fileColumns.forEach((c) => {
      let col = dbColumns.find(
        (dbCol) => dbCol.EntityMetaType.toLowerCase() === c.toLowerCase()
      );

      let entityMetaType = col?.EntityMetaType;
      // the column in excel should be in the selected list
      if (col) {
        if (row[entityMetaType] === null || row[entityMetaType] === undefined) {
          return; // skip null or undefined values, no deletions
        }
        obj[entityMetaType] = [];

        // Handle PO Number validation for Equipment level using utility
        if (!level.length && c.toLowerCase() === 'po number') {
          const extractedPONumber = row['PO Number'].split(' - ')[0].trim();
          if (extractedPONumber) {
            obj['pi_dbid'] = row['PO Number'].substring(13).trim();
            row[entityMetaType] = extractedPONumber; // TODO - we need to find the pi_dbid here and attach like row[pi_dbid]= ''
          }
        }

        // Handle IO Number validation for Equipment level using utility
        if (!level.length && c.toLowerCase() === 'io number') {
          const extractedIONumber = row['IO Number'].split(' - ')[0].trim();
          if (extractedIONumber) {
            row[entityMetaType] = extractedIONumber;
          }
        }

        if (
          (col.DataType === 'Group' || col.DataType === 'Multiple Choice') &&
          col.AllowMultiple === 0
        ) {
          let temp = col.options.find((opt) => {
            return (
              opt.toString().toLowerCase() ===
              row[entityMetaType]?.toString()?.toLowerCase()
            );
          });
          // verify if the provided value is present in meta options or not
          if (temp) {
            row[entityMetaType] = temp;
          } else {
            invalidMeta.push(
              groupSelectError(
                row.rowNum,
                row[entityMetaType],
                col.EntityMetaType
              )
            );
          }
        } else if (
          (col.DataType === 'Group' || col.DataType === 'Multiple Choice') &&
          col.AllowMultiple === 1
        ) {
          // if the grouped values can be multiple, split by "|" and verify each
          let array = row[entityMetaType].toString().split('|');
          let values = [];
          array.forEach((ary) => {
            let temp = col.options.find((opt) => {
              return (
                opt.toString().toLowerCase() === ary.toString().toLowerCase()
              );
            });
            if (temp) {
              values.push(temp);
            } else {
              invalidMeta.push(
                groupSelectError(row.rowNum, ary, col.EntityMetaType)
              );
            }
            row[entityMetaType] = values.join('|');
          });
        }
        // if the field is not group or dropdown but allows multiple, e.g., notes, verify the data
        else if (
          col.DataType !== 'Group' &&
          col.DataType !== 'Multiple Choice' &&
          col.AllowMultiple === 1
        ) {
          let array = row[entityMetaType].toString().split('|');

          array.forEach((ary) => {
            let resultArray = validateField(col, ary, {
              mode: 'bulk',
              rowIndex: row.rowNum,
            }); // Multi string, int, date, phone etc.

            if (resultArray.length > 0) {
              invalidMeta.push(...resultArray);
            }
          });
        } else {
          let resultArray = validateField(col, row[entityMetaType], {
            mode: 'bulk',
            rowIndex: row.rowNum,
          }); // single string, int, date, float etc. type
          if (resultArray.length > 0) {
            invalidMeta.push(...resultArray);
          }
        }
        // if any field is binary, for 0 make it null, otherwise use the data
        if (col.DataType === 'Binary') {
          row[entityMetaType] =
            row[entityMetaType] === 0 ? '' : row[entityMetaType];
        }
        // format the date type entity meta
        if (col.DataType === 'Date') {
          if (col.AllowMultiple === 0) {
            let date = new Date(row[entityMetaType]);
            date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
            date = date.toISOString();
            row[entityMetaType] = date;
          }
        }

        // stringify the json object for dynamic sqlto read based on selected meta type
        obj[col.EntityMetaType] =
          col.AllowMultiple === 1
            ? JSON.stringify([...row[entityMetaType].toString().split('|')])
            : col.DataType
              ? JSON.stringify([row[entityMetaType].toString()])
              : row[entityMetaType];
      }
    });
    // if updating last service date, calculate the next service date
    if (periodicityFetchFlag && obj['Last Service Date']?.length > 0) {
      obj['Periodicity'] = JSON.stringify([row['Periodicity'].toString()]);
      obj = addNextServiceDate(obj, i);
      keys.push('Next Service Date');
    }

    // no location or owner update for groups
    // bulk edit for groups is not implemented
    if (level !== 'Group') {
      if (
        selectedToUpdateFields.filter(
          (field) => field.EntityMetaType === 'Location'
        )?.length
      ) {
        // validate provided location
        obj = validateLocation(obj, i);
      }

      if (
        selectedToUpdateFields.filter(
          (field) => field.EntityMetaType === 'EntityOwner'
        )?.length
      ) {
        // validate provided owner
        obj = validateOwner(obj, i);
      }
    }

    // if there is no error addd the row to be updated
    if (invalidMeta.length === 0) {
      finalJSON.push(obj);
    }
  });
  // excel loop ends
  // if any error, send the report with "Error" status
  if (invalidMeta.length > 0) {
    return { data: invalidMeta, Status: 'Error' };
  } else {
    let fields = selectedToUpdateFields;

    // make a flat list of "to update" fields
    fields.forEach((field) => {
      if (field.DataType) {
        keys.push(field.EntityMetaType);
      }
    });

    // add EntityOwnerName if "Owner" is part of "to update" list
    if (keys.find((val) => val === 'EntityOwner')) {
      keys.push('EntityOwnerName');
    }

    // mark a flag is Location is part of update
    if (fields.find((f) => f.EntityMetaType === 'Location')) {
      isLocation = 1;
    }

    if (fields.find((f) => f.EntityMetaType === 'EntityName')) {
      isName = 1;
    }

    // return the processed data
    return {
      finalJSON: finalJSON,
      keys: keys,
      excelRows: excelRows,
      isName: isName,
      isLocation: isLocation,
      uniqueToUpdateMetaType: uniqueToUpdateMetaType,
      Status: 'Processed',
    };
  }
};

const checkExcelDataUniqueValidity = (toUpdateUnique, excelRows) => {
  let uniqueToUpdateField = toUpdateUnique.uniqueToUpdateField.EntityMetaType;
  let uniqueByField =
    toUpdateUnique.uniqueByField?.EntityMetaType ??
    toUpdateUnique.uniqueToUpdateField.EntityMetaType;
  // check for duplicates in the excel "to update" fields if they are supposed to be unique
  let duplicates = excelRows.filter((row) => {
    return (
      excelRows.filter(
        (r) =>
          r[uniqueToUpdateField] === row[uniqueToUpdateField] &&
          r[uniqueByField] === row[uniqueByField] &&
          row['Entity_DBID'] !== r['Entity_DBID']
      ).length > 1
    );
  });

  // if there are any duplicates, add the rows to the error report
  if (duplicates?.length) {
    let errMsg =
      'Row(s) : ' +
      duplicates.map((row) => row.rowNum + 1) +
      ' - have duplicate (' +
      uniqueToUpdateField;
    let uniqueByErrMsg =
      uniqueByField === uniqueToUpdateField ? ')' : ', ' + uniqueByField + ')';
    invalidMeta.push(errMsg + uniqueByErrMsg);
  }
};

const prepareExcelData = (rows, identifyingFields) => {
  // get the "unique by" entity meta based on header row of excel
  let uniqueBy = {
    uniqueIdentifyField: identifyingFields.filter(
      // add only if all the identifiying fields are present in the list.
      (field) => {
        if (
          identifyingFields.filter(
            (f) => f.EntityMetaType === field.uniqueByEntityMetaType
          )?.length
        )
          return field;
      }
    )[0],
    uniqueByField: identifyingFields.filter(
      (field) =>
        field.label ===
        rows[0].filter((row) => row.includes('(Identified By)'))[0]
    )[0],
  };

  let identifyingList = [];
  let uniqueByIdentifyingList = [];
  let excelRows = [];

  let colList = rows[0];
  let colListIdx = uniqueBy?.uniqueByField ? 4 : 3;
  // convert excel rows into list of objects
  rows.forEach((row, i) => {
    if (i > 0) {
      let obj = {};

      obj['rowNum'] = i;
      obj['type'] = row[0];
      obj['identifier'] = row[1];
      obj['value'] = row[2];
      colList
        .slice(colListIdx)
        .forEach((field, idx) => (obj[field] = row[idx + colListIdx]));

      if (row[1] === uniqueBy?.uniqueIdentifyField?.EntityMetaType) {
        obj['uniqueBy_DBID'] = uniqueBy?.uniqueByField?.EntityMetaType_DBID;
        obj['uniqueByEntityMetaType'] = uniqueBy?.uniqueByField?.EntityMetaType;
        obj['uniqueByValue'] = row[3];
        uniqueByIdentifyingList.push(obj);
      } else identifyingList.push(obj);
      excelRows.push(obj);
    }
  });
  let excelData = {
    identifyingList: identifyingList, // list to identify entities in db
    uniqueByIdentifyingList: uniqueByIdentifyingList, // if applicable, or else, empty
    excelRows: excelRows, // list of objects of excel rows
  };
  return excelData;
};

// provided location in excel must be present in db
const validateLocation = (obj, i) => {
  if (obj['Location'] !== undefined) {
    let location = JSON.parse(obj['Location'])[0];
    let parent = locations.find((loc) => {
      return loc.Path.toLowerCase() === location.toLowerCase();
    });
    if (parent) {
      obj['Location'] = parent.Entity_DBID; // add Path
    } else {
      //invalid location error
      invalidMeta.push('Row Num : ' + (i + 1) + ' - Location is Invalid.');
    }
  }

  return obj;
};

// provided owner in excel must be present in db
const validateOwner = (obj, i) => {
  if (obj.EntityOwner) {
    let owner = JSON.parse(obj.EntityOwner)[0];
    if (owner) {
      let user = users.find((user) => {
        return user.UserName.toLowerCase() === owner.toLowerCase();
      });
      if (user) {
        obj['EntityOwner'] = JSON.stringify([user.User_DBID.toString()]);
        obj['EntityOwnerName'] = JSON.stringify([user.UserName]);
      } else {
        //invalid owner error
        invalidMeta.push('Row Num : ' + (i + 1) + ' - Owner is Invalid.');
      }
    }
  }

  return obj;
};

// calculate next service date based on last service date and periodicity
const addNextServiceDate = (obj, i) => {
  if (obj['Last Service Date'] && obj['Periodicity']) {
    let lastServiceDate = new Date(JSON.parse(obj['Last Service Date'])[0]);
    let periodicity = parseFloat(JSON.parse(obj['Periodicity'])[0]);
    let nextServiceDate = addDateWithYears(lastServiceDate, periodicity);
    let date = new Date(nextServiceDate);
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    date = date.toISOString();
    obj['Next Service Date'] = JSON.stringify([date]);

    // Get local date at midnight for comparison for the date
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (nextServiceDate == 'Invalid Date') {
      invalidMeta.push(
        'Row Num : ' +
          (i + 1) +
          ' - Next Service Date is invalid. Check Last Service Date and Periodicity.'
      );
    }
    if (nextServiceDate instanceof Date && nextServiceDate < today) {
      invalidMeta.push(
        'Row Num : ' + (i + 1) + ' - Next Service Date is in the past.'
      );
    }
  }
  return obj;
};

// download the error report as simple text file
const downloadErrReport = (data) => {
  const blob = new Blob([data], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = 'bulk_edit_error_report' + getDateTime() + '.txt';
  link.href = url;
  link.click();
};

/**
 * Enhanced PO-IO mapping using Excel Tables
 * @param {Object} worksheet - Excel worksheet object
 * @param {Array} poNumbers - Array of PO objects
 * @param {Array} ioNumbers - Array of IO objects
 */
const createAdvancedPOIOMapping = (worksheet, poNumbers, ioNumbers) => {
  // Create headers
  worksheet.getCell('A1').value = 'PO_Key';
  worksheet.getCell('B1').value = 'PO_Clean';
  worksheet.getCell('C1').value = 'IO_Options';
  worksheet.getCell('D1').value = 'IO_Clean';
  worksheet.getCell('E1').value = 'Quantity';

  // Make headers bold
  worksheet.getRow(1).font = { bold: true };

  let currentRow = 2;
  const poIOMap = {};
  // Group IO numbers by PO
  ioNumbers.forEach((io) => {
    const relatedPO = poNumbers.find((po) => po.pi_dbid === io.pi_dbid);
    if (relatedPO) {
      const poKey = relatedPO.pi_dbid;
      const poDisplay = `${relatedPO.po_num}${relatedPO.pi_description ? ` - ${relatedPO.pi_description}` : ''}`;
      const poClean = relatedPO.po_num;
      const qty = relatedPO.qty;

      if (!poIOMap[poKey]) {
        poIOMap[poKey] = {
          poClean: poClean,
          poDisplay: poDisplay,
          qty: qty,
          ios: [],
        };
      }

      poIOMap[poKey].ios.push({
        display: `${io.io_num}${io.io_description ? ` - ${io.io_description}` : ''}`,
        clean: io.io_num,
      });
    }
  });

  // Write mapping data in a structured way
  Object.keys(poIOMap).forEach((poKey) => {
    const poData = poIOMap[poKey];
    poData.ios.forEach((io) => {
      worksheet.getCell(`A${currentRow}`).value = poData.poDisplay;
      worksheet.getCell(`B${currentRow}`).value = poData.poClean;
      worksheet.getCell(`C${currentRow}`).value = io.display;
      worksheet.getCell(`D${currentRow}`).value = io.clean;
      worksheet.getCell(`E${currentRow}`).value = poData.qty;
      currentRow++;
    });
  });

  // Only create table if we have data rows
  if (currentRow > 2) {
    // Ensure all cells in the range are properly defined
    for (let row = 1; row < currentRow; row++) {
      for (let col = 1; col <= 4; col++) {
        const cell = worksheet.getCell(row, col);
        if (cell.value === null || cell.value === undefined) {
          cell.value = ''; // Set empty string for undefined cells
        }
      }
    }
    createFormattedRange(worksheet, currentRow);
  } else {
    // If no data, create a simple message
    worksheet.getCell('A2').value = 'No PO-IO mappings available';
    worksheet.getCell('B2').value = '';
    worksheet.getCell('C2').value = '';
    worksheet.getCell('D2').value = '';
    worksheet.getCell('E2').value = '';

    // Style the message row
    worksheet.getRow(2).font = { italic: true, color: { argb: 'FF666666' } };
    worksheet.getCell('A2').alignment = { horizontal: 'center' };

    // Merge the message across all columns
    worksheet.mergeCells('A2:E2');

    // Still apply basic formatting to headers
    createFormattedRange(worksheet, 3);
  }
};

/**
 * Create formatted range for PO-IO mapping
 * @param {Object} worksheet - Excel worksheet object
 * @param {number} currentRow - Current row number
 */
const createFormattedRange = (worksheet, currentRow) => {
  try {
    // Style the header row
    for (let col = 1; col <= 5; col++) {
      const headerCell = worksheet.getCell(1, col);
      headerCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF366092' },
      };
      headerCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    }

    // Style data rows if they exist
    for (let row = 2; row < currentRow; row++) {
      for (let col = 1; col <= 5; col++) {
        const cell = worksheet.getCell(row, col);
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };

        // Alternate row colors
        if (row % 2 === 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF8F9FA' },
          };
        }
      }
    }
  } catch (error) {
    console.warn('Could not format range:', error);
  }
};

/**
 * Enhanced IO validation using OFFSET instead of INDIRECT
 * @param {Object} worksheet - Excel worksheet object
 * @param {number} ioColumnPosition - Position of IO column
 * @param {Array} poNumbers - Array of PO objects
 * @param {Array} ioNumbers - Array of IO objects
 */
const createAdvancedIOValidation = (
  worksheet,
  ioColumnPosition,
  poNumbers,
  ioNumbers
) => {
  const ioColumnLetter = worksheet.getColumn(ioColumnPosition).letter;

  // Create a comprehensive lookup table in validation sheet
  const validationSheet = worksheet.workbook.getWorksheet('validation');
  createValidationLookupTable(validationSheet, poNumbers, ioNumbers);

  // Apply data validation using VLOOKUP instead of INDIRECT
  const ioRange = `${ioColumnLetter}2:${ioColumnLetter}1000`;

  try {
    // Use a more stable validation approach
    const allIOOptions = ioNumbers.map((io) => {
      const description = io.io_description ? ` - ${io.io_description}` : '';
      return `${io.io_num}${description}`;
    });

    // Add all IOs to validation sheet
    const validationColumn = validationSheet.getColumn(ioColumnPosition + 5);
    validationColumn.values = ['All_IOs', ...allIOOptions];

    worksheet.dataValidations.add(ioRange, {
      type: 'list',
      allowBlank: true,
      formulae: [
        `validation!$${validationColumn.letter}$2:$${validationColumn.letter}$${allIOOptions.length + 1}`,
      ],
      showErrorMessage: true,
      errorStyle: 'warning',
      error:
        'Please select a valid IO number. Check Instructions sheet for PO-IO mappings.',
    });
  } catch (error) {
    console.warn('Could not create validation:', error);
  }
};

/**
 * Create a lookup table in validation sheet
 * @param {Object} validationSheet - Validation worksheet object
 * @param {Array} poNumbers - Array of PO objects
 * @param {Array} ioNumbers - Array of IO objects
 */
const createValidationLookupTable = (validationSheet, poNumbers, ioNumbers) => {
  let startColumn = 10; // Start from column J

  // Create PO-IO mapping table
  validationSheet.getCell(
    `${validationSheet.getColumn(startColumn).letter}1`
  ).value = 'PO_Number';
  validationSheet.getCell(
    `${validationSheet.getColumn(startColumn + 1).letter}1`
  ).value = 'Valid_IOs';

  let currentRow = 2;
  const poIOMap = {};

  // Group IOs by PO
  ioNumbers.forEach((io) => {
    const relatedPO = poNumbers.find((po) => po.po_dbid === io.po_dbid);
    if (relatedPO) {
      const poKey = `${relatedPO.po_num}${relatedPO.pi_description ? ` - ${relatedPO.pi_description}` : ''}`;
      if (!poIOMap[poKey]) {
        poIOMap[poKey] = [];
      }
      poIOMap[poKey].push(
        `${io.io_num}${io.io_description ? ` - ${io.io_description}` : ''}`
      );
    }
  });

  // Write the mapping
  Object.keys(poIOMap).forEach((poKey) => {
    validationSheet.getCell(
      `${validationSheet.getColumn(startColumn).letter}${currentRow}`
    ).value = poKey;
    validationSheet.getCell(
      `${validationSheet.getColumn(startColumn + 1).letter}${currentRow}`
    ).value = poIOMap[poKey].join('; ');
    currentRow++;
  });
};

/**
 * Enhanced validation during upload processing with quantity validation
 * @param {Array} finalJSON - Array of processed records
 * @param {Function} addError - Function to add validation errors
 * @returns {Promise} Promise that resolves when validation is complete
 */
/**
 * Optimized validation during upload processing with quantity validation
 * @param {Array} finalJSON - Array of processed records
 * @param {Function} addError - Function to add validation errors
 * @returns {Promise} Promise that resolves when validation is complete
 */
const validatePOIORelationships = (finalJSON, addError) => {
  return new Promise((resolve, reject) => {
    // Parallel API calls for better performance
    Promise.all([
      Api({ sp: 'getInternalOrders', json: {} }),
      Api({ sp: 'getPurchaseOrders', json: {} }),
    ])
      .then(([allIONumbers, allPONumbers]) => {
        // Create lookup maps for O(1) access instead of O(n) find operations
        const poLookupByNumber = new Map();
        const ioLookupByPIDBID = new Map();

        // Build PO lookup map
        allPONumbers.forEach((po) => {
          if (!poLookupByNumber.has(po.po_num)) {
            poLookupByNumber.set(po.po_num, []);
          }
          poLookupByNumber.get(po.po_num).push(po);
        });

        // Build IO lookup map grouped by pi_dbid
        allIONumbers.forEach((io) => {
          if (!ioLookupByPIDBID.has(io.pi_dbid)) {
            ioLookupByPIDBID.set(io.pi_dbid, []);
          }
          ioLookupByPIDBID.get(io.pi_dbid).push(io);
        });

        // Group upload items by pi_dbid and validate in single pass
        const uploadQuantitiesByPIDBID = new Map();
        const validationErrors = [];

        // Single pass through finalJSON for grouping and initial validation
        finalJSON.forEach((item, index) => {
          const validationResult = validateSingleItem(
            item,
            index,
            poLookupByNumber,
            ioLookupByPIDBID,
            uploadQuantitiesByPIDBID
          );

          if (validationResult.errors.length > 0) {
            validationErrors.push(...validationResult.errors);
          }
        });

        // Batch quantity validation for all pi_dbids
        const quantityErrors = validateQuantityLimits(uploadQuantitiesByPIDBID);
        validationErrors.push(...quantityErrors);

        // Add all errors at once
        validationErrors.forEach((error) => addError(error));

        resolve();
      })
      .catch(reject);
  });
};

/**
 * Validate a single item and update quantity tracking
 * @param {Object} item - Item to validate
 * @param {number} index - Item index
 * @param {Map} poLookupByNumber - PO lookup map
 * @param {Map} ioLookupByPIDBID - IO lookup map
 * @param {Map} uploadQuantitiesByPIDBID - Quantity tracking map
 * @returns {Object} Validation result with errors array
 */
const validateSingleItem = (
  item,
  index,
  poLookupByNumber,
  ioLookupByPIDBID,
  uploadQuantitiesByPIDBID
) => {
  const errors = [];

  // Skip items without PO Number
  if (!item['PO Number']) {
    // Only validate capital asset requirements if no PO Number
    if (item.isCapital === true) {
      errors.push(
        `Row ${index + 2}: PO Number is mandatory for capital assets`
      );
    }
    return { errors };
  }

  const poNumber = Number(JSON.parse(item['PO Number'])[0]);
  const relatedPOs = poLookupByNumber.get(poNumber) || [];

  const relatedPO = relatedPOs.find(
    (po) => po.pi_description === item['pi_dbid']
  );

  if (!relatedPO) {
    errors.push(
      `Row ${index + 2}: PO Number "${poNumber}" is not valid. Please check the PO-IO Mapping sheet for valid PO numbers.`
    );
    return { errors };
  }

  // Add pi_dbid and quantity information to item
  const pi_dbid = relatedPO.pi_dbid;
  item['pi_dbid'] = pi_dbid;

  const availableQuantity = relatedPO.qty || 0;

  item['po_available_quantity'] = availableQuantity;

  // Update quantity tracking
  if (!uploadQuantitiesByPIDBID.has(pi_dbid)) {
    uploadQuantitiesByPIDBID.set(pi_dbid, {
      count: 0,
      items: [],
      rows: [],
      poNumber: poNumber,
      availableQuantity: availableQuantity,
    });
  }

  const quantityData = uploadQuantitiesByPIDBID.get(pi_dbid);
  quantityData.count += 1;
  quantityData.items.push(item);
  quantityData.rows.push(index);

  // Validate individual item availability
  if (availableQuantity <= 0) {
    errors.push(`Row ${index + 2}: PO ${poNumber} has no available quantity.`);
  }

  // Validate IO Number if provided
  if (item['IO Number']) {
    const ioNumber = Number(JSON.parse(item['IO Number'])[0]);
    const validIOs = ioLookupByPIDBID.get(pi_dbid) || [];

    const validIO = validIOs.find((io) => io.io_num === ioNumber);

    if (!validIO) {
      const validIONumbers = validIOs.map((io) => io.io_num);
      errors.push(
        `Row ${index + 2}: IO Number "${ioNumber}" does not belong to PO "${poNumber}"  Valid IOs for this Purchase Item: ${validIONumbers.join(', ')}`
      );
    }
  }

  // Capital asset validation
  if (item.isCapital === true) {
    if (!item['IO Number']) {
      errors.push(
        `Row ${index + 2}: IO Number is mandatory for capital assets`
      );
    }
  }

  return { errors };
};

/**
 * Validate quantity limits for all purchase items
 * @param {Map} uploadQuantitiesByPIDBID - Quantity tracking map
 * @returns {Array} Array of quantity validation errors
 */
const validateQuantityLimits = (uploadQuantitiesByPIDBID) => {
  const errors = [];

  uploadQuantitiesByPIDBID.forEach((quantityData) => {
    const { count, rows, poNumber, availableQuantity } = quantityData;

    if (count > availableQuantity) {
      const affectedRows = rows.map((r) => r + 2).join(', ');
      errors.push(
        `PO ${poNumber} : Upload contains ${count} items but only ${availableQuantity} available. ` +
          `Affected rows: ${affectedRows}`
      );
    }
  });

  return errors;
};
/**
 * Process capital asset validation with PO/IO mutual requirements
 * @param {Object} row - Current row data
 * @param {Array} fileColumns - File column headers
 * @param {number} rowIndex - Current row index
 * @param {Function} addError - Function to add validation errors
 * @returns {boolean} isCapital value
 */
const processCapitalAssetValidation = (
  row,
  fileColumns,
  rowIndex,
  addError
) => {
  let capitalError = false;
  const cellIndex = fileColumns.findIndex(
    (fc) => fc.toLowerCase() === 'iscapital'
  );

  // Find PO Number and IO Number column indices
  const poColumnIndex = fileColumns.findIndex(
    (fc) => fc.toLowerCase() === 'po number'
  );
  const ioColumnIndex = fileColumns.findIndex(
    (fc) => fc.toLowerCase() === 'io number'
  );
  const ctagColumnIndex = fileColumns.findIndex(
    (fc) => fc.toLowerCase() === 'ctag'
  );

  // Check if PO Number and IO Number have values
  const hasPONumber =
    poColumnIndex !== -1 &&
    row[poColumnIndex] !== null &&
    row[poColumnIndex] !== undefined &&
    row[poColumnIndex].toString().trim() !== '';

  const hasIONumber =
    ioColumnIndex !== -1 &&
    row[ioColumnIndex] !== null &&
    row[ioColumnIndex] !== undefined &&
    row[ioColumnIndex].toString().trim() !== '';

  const hasCTag =
    ctagColumnIndex !== -1 &&
    row[ctagColumnIndex] !== null &&
    row[ctagColumnIndex] !== undefined &&
    row[ctagColumnIndex].toString().trim() !== '';

  // Validation: CTag, IO Number, and PO Number must all be provided if any one is entered
  const fieldsPresent = [hasCTag, hasIONumber, hasPONumber];
  const anyPresent = fieldsPresent.some((present) => present);
  const allPresent = fieldsPresent.every((present) => present);

  if (anyPresent && !allPresent) {
    const missingFields = [];
    if (!hasCTag) missingFields.push('CTag');
    if (!hasIONumber) missingFields.push('IO Number');
    if (!hasPONumber) missingFields.push('PO Number');
    capitalError = true;
    addError(
      `Row Num : ${rowIndex + 1} - CTag, IO Number, and PO Number are all required if any one is entered. Missing: ${missingFields.join(', ')}`
    );
  }

  // Process isCapital validation if column exists
  if (
    !capitalError &&
    cellIndex !== -1 &&
    row[cellIndex] !== null &&
    row[cellIndex] !== undefined
  ) {
    const isCapitalValue = row[cellIndex].toString().toLowerCase();
    const isCapital = isCapitalValue === 'yes' || isCapitalValue === 'true';

    if (isCapital) {
      // Enhanced validation for capital assets
      if (!hasPONumber) {
        addError(
          `Row Num : ${rowIndex + 1} - PO Number is required for capital assets`
        );
      }

      if (!hasIONumber) {
        addError(
          `Row Num : ${rowIndex + 1} - IO Number is required for capital assets`
        );
      }

      if (!hasCTag) {
        addError(
          `Row Num : ${rowIndex + 1} - CTag is required for capital assets`
        );
      }
    }

    return isCapital;
  } else {
    // If no isCapital column but PO/IO are provided, assume it might be capital-related
    if (hasPONumber || hasIONumber) {
      return true;
    }
  }

  return false;
};

/**
 * Process PO Number validation and extraction
 * @param {Object} row - Current row data
 * @param {Array} fileColumns - File column headers
 * @returns {string|null} Extracted PO Item Description
 */
const extractPurchaseItem = (row, fileColumns) => {
  const cellIndex = fileColumns.findIndex(
    (fc) => fc.toLowerCase() === 'po number'
  );
  if (
    cellIndex !== -1 &&
    row[cellIndex] !== null &&
    row[cellIndex] !== undefined
  ) {
    const poValue = row[cellIndex].toString();
    return poValue.substring(13).trim();
  }

  return null;
};
/**
 * Process PO Number validation and extraction
 * @param {Object} row - Current row data
 * @param {Array} fileColumns - File column headers
 * @returns {string|null} Extracted PO number
 */
const processPONumber = (row, fileColumns) => {
  const cellIndex = fileColumns.findIndex(
    (fc) => fc.toLowerCase() === 'po number'
  );

  if (
    cellIndex !== -1 &&
    row[cellIndex] !== null &&
    row[cellIndex] !== undefined
  ) {
    const poValue = row[cellIndex].toString();
    return poValue.split(' - ')[0].trim();
  }

  return null;
};

/**
 * Process IO Number validation and extraction
 * @param {Object} row - Current row data
 * @param {Array} fileColumns - File column headers
 * @returns {string|null} Extracted IO number
 */
const processIONumber = (row, fileColumns) => {
  const cellIndex = fileColumns.findIndex(
    (fc) => fc.toLowerCase() === 'io number'
  );

  if (
    cellIndex !== -1 &&
    row[cellIndex] !== null &&
    row[cellIndex] !== undefined
  ) {
    const ioValue = row[cellIndex].toString();
    return ioValue.split(' - ')[0].trim();
  }

  return null;
};

/**
 * Add equipment level hint rows
 * @param {Array} hint - Existing hint array
 * @param {boolean} isEquipmentLevel - Whether this is equipment level
 * @returns {Array} Updated hint array
 */
const addEquipmentHints = (hint, isEquipmentLevel) => {
  if (isEquipmentLevel) {
    return [
      'Select option',
      'Select PO Number',
      'Select IO Number (depends on PO)',
      'Text...',
      ...hint,
    ];
  }
  return hint;
};

/**
 * Add equipment level columns to dbColumns
 * @param {Array} dbColumns - Existing database columns
 * @param {boolean} isEquipmentLevel - Whether this is equipment level
 * @returns {Array} Updated dbColumns array
 */
const addEquipmentColumns = (dbColumns, isEquipmentLevel) => {
  if (isEquipmentLevel) {
    const equipmentColumns = [
      {
        EntityMetaType: 'isCapital',
        DataType: 'String',
        AllowMultiple: 0,
      },
      {
        EntityMetaType: 'PO Number',
        DataType: 'Integer',
      },
      {
        EntityMetaType: 'IO Number',
        DataType: 'Integer',
      },
      {
        EntityMetaType: 'CTag',
        DataType: 'String',
      },
      {
        EntityMetaType: 'pi_dbid',
      },
    ];

    return [...equipmentColumns, ...dbColumns];
  }

  return dbColumns;
};

/**
 * Downloads an Excel file for SAP/Asset Number updates
 * @param {Array} sapNumberUpdate - Array of objects containing SAP number update information
 * @param {string} reportType - Type of report (default: 'SAP Number')
 */
const downloadAssetNumberUpdateReport = (
  sapNumberUpdate,
  reportType = 'SAP Number'
) => {
  try {
    // Validate input data
    if (
      !sapNumberUpdate ||
      !Array.isArray(sapNumberUpdate) ||
      sapNumberUpdate.length === 0
    ) {
      toast.warning(
        `No ${reportType.toLowerCase()} update data available for download`
      );
      return;
    }

    console.log(
      `Generating ${reportType} Update Excel report...`,
      sapNumberUpdate
    );

    // Create a new workbook and worksheet
    const workbook = new Excel.Workbook();
    const worksheet = workbook.addWorksheet(`${reportType} Updates`);

    // Define the columns based on the object structure
    const columns = [
      { header: 'Row Number', key: 'rowNum', width: 12 },
      { header: `Old ${reportType}`, key: 'oldSAPNumber', width: 20 },
      { header: `New ${reportType}`, key: 'newSAPNumber', width: 20 },
      { header: 'Identifier', key: 'identifier', width: 15 },
      { header: 'Identifier Value', key: 'idValue', width: 20 },
      { header: 'Owner', key: 'user', width: 25 },
      { header: 'Owner Email', key: 'email', width: 30 },
    ];

    // Set worksheet columns
    worksheet.columns = columns;

    // Style the header row
    const headerRow = worksheet.getRow(1);
    headerRow.font = {
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF366092' },
    };
    headerRow.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };

    // Add data rows
    sapNumberUpdate.forEach((item, index) => {
      const row = worksheet.addRow({
        rowNum: item.rowNum || index + 1,
        oldSAPNumber: item.oldSAPNumber || 'N/A',
        newSAPNumber: item.newSAPNumber || 'N/A',
        identifier: item.identifier || 'N/A',
        idValue: item.idValue || 'N/A',
        user: item.user || 'N/A',
        email: item.email || 'N/A',
      });

      // Style data rows with alternating colors
      if ((index + 2) % 2 === 0) {
        row.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFF8F9FA' },
        };
      }

      // Add borders to all cells
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    // Add summary information
    const summaryRowIndex = sapNumberUpdate.length + 3;

    // Add summary header
    const summaryHeaderRow = worksheet.getRow(summaryRowIndex);
    summaryHeaderRow.getCell(1).value = 'Summary';
    summaryHeaderRow.font = { bold: true, size: 14 };

    // Add summary details
    const summaryDetailsRow = worksheet.getRow(summaryRowIndex + 1);
    summaryDetailsRow.getCell(1).value =
      `Total ${reportType} Updates: ${sapNumberUpdate.length}`;
    summaryDetailsRow.getCell(3).value =
      `Generated on: ${new Date().toLocaleString()}`;

    const noteRow = worksheet.getRow(summaryRowIndex + 2);
    noteRow.getCell(1).value =
      `SAP Asset Number for above asset list was updated in ELM with correct value received from SAP system. Please Notify Owners to verify`;

    // Auto-fit columns
    worksheet.columns.forEach((column) => {
      column.width = Math.max(column.width || 10, 15);
    });

    // Generate filename with timestamp
    const timestamp = getDateTime();
    const filename = `${reportType.replace(/\s+/g, '_')}_Updates_${timestamp}.xlsx`;

    // Generate Excel file buffer and trigger download
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      FileSaver.saveAs(blob, filename);

      toast.success(`${reportType} Update report downloaded: ${filename}`);
      console.log(`Excel file generated successfully: ${filename}`);
    });
  } catch (error) {
    console.error(`Error generating ${reportType} Update Excel:`, error);
    toast.error(
      `Failed to generate ${reportType} Update report. Please try again.`
    );
  }
};
/**
 * Adds audit trail comment to entities with SAP Number updates during bulk edit operations
 * @param {Object} result - Bulk edit result containing finalJSON array of entities
 * @returns {Array} Updated entity array with EntityComment field added for SAP Number changes
 */ const updateCommentForSAPNumber = (result) => {
  return result.finalJSON.map((item) => {
    if (item['SAP Number']) {
      return {
        ...item,
        EntityComment: JSON.stringify([
          'SAP Number update done by Admin from SAP System data',
        ]),
      };
    } else {
      return item;
    }
  });
};

const validateCTagExistence = (capAssetCols, excelRows) => {
  return Api({
    sp: 'checkCTagExistence',
    json: {
      capitalAssetFields: capAssetCols,
      excelRows: excelRows,
    },
  })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      console.error('Error validating CTag existence:', error);
      return;
    });
};

const validatePOIOExistence = (capAssetCols, excelRows) => {
  return Api({
    sp: 'checkPOIOExistence',
    json: {
      capitalAssetFields: capAssetCols,
      excelRows: excelRows,
    },
  })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      console.error('Error validating PO/IO existence:', error);
      return;
    });
};

export {
  templateDownloadBulkEdit,
  processUploadBulkEdit,
  downloadErrReport,
  prepareExcelData,
  addNextServiceDate,
  preprocessPOData,
  createAdvancedPOIOMapping,
  createFormattedRange,
  createAdvancedIOValidation,
  createValidationLookupTable,
  validatePOIORelationships,
  processCapitalAssetValidation,
  processPONumber,
  processIONumber,
  addEquipmentHints,
  addEquipmentColumns,
  removeCapitalAssetColumns,
  extractPurchaseItem,
  downloadAssetNumberUpdateReport,
  updateCommentForSAPNumber,
  validateCTagExistence,
  validatePOIOExistence,
};
